import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import $axios from './index';

const withPassword = (config, adminPassword) => {
  const c = { ...config };
  c.headers = { ...c.headers, 'X-Admin-Password': adminPassword };
  return c;
};

const serializableError = (err) => {
  if (!err) return null;
  const res = err.response;
  if (res) return { status: res.status, data: res.data };
  return { message: err.message || 'Request failed' };
};

export const getPermissions = createAsyncThunk('roleManagement/getPermissions', async () => {
  try {
    const res = await $axios.get('/role-management/permissions');
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const getRoles = createAsyncThunk('roleManagement/getRoles', async () => {
  try {
    const res = await $axios.get('/role-management/roles');
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const createRole = createAsyncThunk('roleManagement/createRole', async ({ data, adminPassword }) => {
  try {
    const res = await $axios.post('/role-management/roles', data, withPassword({}, adminPassword));
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const updateRole = createAsyncThunk('roleManagement/updateRole', async ({ roleId, data, adminPassword }) => {
  try {
    const res = await $axios.put(`/role-management/roles/${roleId}`, data, withPassword({}, adminPassword));
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const deleteRole = createAsyncThunk('roleManagement/deleteRole', async ({ roleId, adminPassword }) => {
  try {
    await $axios.delete(`/role-management/roles/${roleId}`, withPassword({}, adminPassword));
    return [true, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const getAdminUsers = createAsyncThunk('roleManagement/getAdminUsers', async () => {
  try {
    const res = await $axios.get('/role-management/admin-users');
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const createAdminUser = createAsyncThunk('roleManagement/createAdminUser', async ({ data, adminPassword }) => {
  try {
    const res = await $axios.post('/role-management/admin-users', { ...data, adminPassword }, withPassword({}, adminPassword));
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const updateAdminUser = createAsyncThunk('roleManagement/updateAdminUser', async ({ userId, data, adminPassword }) => {
  try {
    const res = await $axios.patch(`/role-management/admin-users/${userId}`, { ...data, adminPassword }, withPassword({}, adminPassword));
    return [res.data.data, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

export const deleteAdminUser = createAsyncThunk('roleManagement/deleteAdminUser', async ({ userId, adminPassword }) => {
  try {
    await $axios.delete(`/role-management/admin-users/${userId}`, withPassword({}, adminPassword));
    return [true, null];
  } catch (err) {
    return [null, serializableError(err)];
  }
});

const roleManagementSlice = createSlice({
  name: 'roleManagement',
  initialState: {
    permissions: null,
    roles: [],
    adminUsers: [],
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPermissions.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        state.permissions = data || null;
        state.error = err;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        state.roles = data || [];
        state.error = err;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        if (data) state.roles = [...state.roles, data];
        state.error = err;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        if (data) {
          state.roles = state.roles.map((r) => (r._id === data._id ? data : r));
        }
        state.error = err;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        const [ok, err] = action.payload;
        if (ok && action.meta?.arg?.roleId) {
          state.roles = state.roles.filter((r) => r._id !== action.meta.arg.roleId);
        }
        state.error = err;
      })
      .addCase(getAdminUsers.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        state.adminUsers = data || [];
        state.error = err;
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        if (data) state.adminUsers = [...state.adminUsers, data];
        state.error = err;
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        const [data, err] = action.payload;
        if (data) {
          state.adminUsers = state.adminUsers.map((u) => (u._id === data._id ? data : u));
        }
        state.error = err;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        const [ok, err] = action.payload;
        if (ok && action.meta?.arg?.userId) {
          state.adminUsers = state.adminUsers.filter((u) => u._id !== action.meta.arg.userId);
        }
        state.error = err;
      });
  },
});

export const { clearError } = roleManagementSlice.actions;
export default roleManagementSlice.reducer;
